import React, { PureComponent } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { styled } from '@compiled/react';
import type { ApolloQueryResult } from 'apollo-client';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	DropdownItem as AkDropdownItem,
	DropdownItemGroup as AkDropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';

// Helpers / Constants
import { EDITOR2_SHORTCUTS, getShortcutString } from '@confluence/shortcuts';
import { EditLabelDropdownItem } from '@confluence/labels';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { ViewValues } from '@confluence/comments-panel-utils';
import { DialogsStateContainer } from '@confluence/dialogs';
import { useWebItemLocation } from '@confluence/web-item-location';
import {
	ResolvedInlineCommentsCounterLoader,
	ResolvedInlineCommentsDialogLoader,
} from '@confluence/resolved-inline-comments-dialog';
import { ViewChangesDialogLoader } from '@confluence/view-changes-dialog';
import { EditorMovePageButtonLoader } from '@confluence/editor-move-page-button';
import { getApolloClient, markErrorAsHandled } from '@confluence/graphql';
import { isUnauthorizedError, Attribution } from '@confluence/error-boundary';
import { containsNoNetworkError } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring';
import { RevertEditorDialog } from '@confluence/editor-conversion/entry-points/RevertEditorDialog';
import { getAutoConvertionInfo } from '@confluence/editor-conversion';
import type { ContentPropertiesQueryType } from '@confluence/content-body/entry-points/ContentPropertiesQuery';
import { ContentPropertiesQuery } from '@confluence/content-body/entry-points/ContentPropertiesQuery';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { LoadableLazy } from '@confluence/loadable';
import { VIEW_PAGE } from '@confluence/named-routes';
import { useSwitchToLivePage } from '@confluence/live-pages-features/entry-points/useSwitchToLivePage';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import {
	useIsDataClassificationEnabledForOrg,
	EllipsisClassificationMenuItem,
} from '@confluence/data-classification/entry-points/ellipsisClassification';
import { fg } from '@confluence/feature-gating';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { withFlags } from '@confluence/flags';

import { Buttons } from './types';
import type { EllipsisDropdownComponentProps } from './types';
import { onClickPageRevert } from './helpers';
import { CommonDropdownMenu } from './CommonDropdownMenu';
import { i18n as sharedi18n } from './sharedDropdowni18n';
import { useCompanyHubSettingsGroupMenuItems } from './useCompanyHubSettingsGroupMenuItems';

export const i18n = defineMessages({
	preview: {
		id: 'editor-ellipsis-dropdown.preview',
		description: 'The label of Preview button inside ellipsis dropdown of the editor',
		defaultMessage: 'Preview',
	},
	templateDocs: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.templates',
		description: 'The label of Templates button inside ellipsis dropdown of the editor',
		defaultMessage: 'Templates',
	},
	addLabels: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.edit.labels',
		description: 'The label of Add labels button inside ellipsis dropdown of the editor',
		defaultMessage: 'Add labels',
	},
	deleteUnpublishedPage: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.page.delete',
		description:
			'The label of Delete unpublished page button inside ellipsis dropdown of the editor',
		defaultMessage: 'Delete unpublished page',
	},
	deleteUnpublishedBlogpost: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.blogpost.delete',
		description:
			'The label of Delete unpublished blog post button inside ellipsis dropdown of the editor',
		defaultMessage: 'Delete unpublished blog post',
	},
	revert: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.revert',
		description:
			'The label of Delete unpublished blog post button inside ellipsis dropdown of the editor',
		defaultMessage: 'Revert to previous version',
	},
	updateWithoutNotifying: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.update.without.notify',
		description:
			'The label of Update without notifying watchers button inside ellipsis dropdown of the editor',
		defaultMessage: 'Update without notifying watchers',
	},
	openTemplateBrowser: {
		id: 'editor-ellipsis-dropdown.open.template.browser',
		defaultMessage: 'Open template browser',
	},
	revertEditor: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.revert.editor',
		description: 'The label to revert the page from Fabric to Tiny',
		defaultMessage: 'Revert to legacy editor',
	},
	undo: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.editor.undo',
		description: 'The label to undo edit changes on the page',
		defaultMessage: 'Undo',
	},
	redo: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.editor.redo',
		description: 'The label to redo edit changes on the page',
		defaultMessage: 'Redo',
	},
	findReplace: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.editor.find.replace',
		description: 'The label to find and replace text on the page',
		defaultMessage: 'Find and replace',
	},
});

const LivePagesExpandedEapConversionSpotlightLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LivePagesExpandedEapConversionSpotlight" */ './LivePagesExpandedEapConversionSpotlight'
			)
		).LivePagesExpandedEapConversionSpotlight,
});

const noop = () => {};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuItemContainer = styled.span({
	display: 'flex',
	justifyContent: 'space-between',
});

const previewPage = (createAnalyticsEvent, onPreviewClicked, contentId, contentType) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'previewPage',
			source: 'ellipsisDropdownComponent',
			objectId: contentId,
			objectType: contentType,
		},
	}).fire();

	onPreviewClicked(false);
};

const publishWithoutNotify = (createAnalyticsEvent, onClickPublish, contentId, contentType) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'publishWithoutNotifyFromOverflow',
			source: 'ellipsisDropdownComponent',
			objectId: contentId,
			objectType: contentType,
		},
	}).fire();

	onClickPublish({ suppressNotifyWatchers: true })();
};

class EllipsisDropdownClass extends PureComponent<
	EllipsisDropdownComponentProps &
		WithAnalyticsEventsProps & {
			addCompanyHubSettingsGroupMenuItems: ReturnType<typeof useCompanyHubSettingsGroupMenuItems>;
			isLivePagesEnabled: boolean;
			isClassificationEnabled: boolean;
			push: (url: string) => void;
			isInitialMenuOpen: boolean;
			switchToLivePage: () => Promise<void>;
			showCommentsPanel: ({ openWithView }: { openWithView: ViewValues }) => void;
		},
	{
		isMenuOpen: boolean;
		isPageAutoconverted: boolean;
	}
> {
	context: any;
	static displayName = 'EllipsisDropdown';

	static defaultProps = {
		saveDraft: noop,
		isDisabled: false,
		isInitialMenuOpen: false,
	};

	state = {
		isMenuOpen: this.props.isInitialMenuOpen,
		isPageAutoconverted: false,
	};

	componentDidMount() {
		void this.setEditorProperty();
	}

	/**
	 * Set editor property state
	 * @returns {Promise<void>}
	 */
	setEditorProperty = async (): Promise<void> => {
		const { contentId } = this.props;
		const {
			data: contentPropertiesData,
		}: ApolloQueryResult<ContentPropertiesQueryType> | { data: null } = await getApolloClient()
			.query({
				query: ContentPropertiesQuery,
				variables: { contentId },
			})
			.catch((e) => {
				if (isUnauthorizedError(e) || containsNoNetworkError(e)) {
					markErrorAsHandled(e);
				} else {
					getMonitoringClient().submitError(e, {
						attribution: Attribution.BACKBONE,
					});
				}
				return { data: null };
			});

		if (contentPropertiesData) {
			const { editorProperty } = getAutoConvertionInfo(contentPropertiesData?.content?.nodes?.[0]);
			const isNewPage = contentPropertiesData?.content?.nodes?.length === 0;

			this.setState({
				isPageAutoconverted: !editorProperty && !isNewPage,
			});
		}
	};

	saveDraftAnd = (afterSaveDraftDispatched) => () => {
		if (this.props.saveDraft) {
			void this.props.saveDraft();
		}
		afterSaveDraftDispatched();
	};

	// when allowed ellipsis buttons is undefined, enable all buttons
	isAllowed = (menuItem: Buttons) => this.props.allowedEllipsisButtons?.includes(menuItem) ?? true;

	handleMenuOpenChange = (attrs) => this.setState({ isMenuOpen: attrs.isOpen });
	/**
	 * Menu items are pushed into the 'items' list which is used to create the '•••' menu
	 * (aka more aka ellipsis aka meatball) for editor pages
	 */
	_getItems(dialogs) {
		const {
			addCompanyHubSettingsGroupMenuItems,
			contentType,
			isDisabled,
			isUnpublishedDraft,
			showRevertDialog,
			spaceKey,
			contentId,
			isContentCreationPanelShown,
			onClickOpenContentCreationPanel,
			onClickPublish,
			retrieveEditorTitleAndContent,
			onPreviewClicked,
			createAnalyticsEvent,
			isImportDisabled,
			push,
			saveDraft,
			switchToLivePage,
			showCommentsPanel,
			hasEditPermission,
			editorActions,
		} = this.props;

		if (this.state.isMenuOpen) {
			createAnalyticsEvent?.({
				type: 'sendScreenEvent',
				data: {
					name: 'ellipsisDropdownForMoreItems',
				},
			}).fire();
		}

		// Don't want to trigger exposures when users open the ellipsis dropdown we are
		// just disabling an item that is not required for the experiment
		// The exposure will happen when the user converts a page instead
		const [onEditConvertExp] = UNSAFE_noExposureExp('platform_editor_on_edit_convert');

		const addPreviewMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem
					key="preview"
					onClick={() => {
						previewPage(createAnalyticsEvent, onPreviewClicked, contentId, contentType);
					}}
					elemAfter={
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<span style={{ color: token('color.icon.subtle') }}>
							{getShortcutString(EDITOR2_SHORTCUTS.PREVIEW)}
						</span>
					}
				>
					<FormattedMessage {...i18n.preview} />
				</AkDropdownItem>,
			);
		};

		const showViewChangesDialog = () =>
			dialogs.showDialog(ViewChangesDialogLoader, {
				contentId,
			});

		const addViewChangesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="view-changes" onClick={this.saveDraftAnd(showViewChangesDialog)}>
					<FormattedMessage {...sharedi18n.viewChanges} />
				</AkDropdownItem>,
			);
		};

		const addTemplateBrowserMenuItem = (menuItems: JSX.Element[]) => {
			const label = isImportDisabled ? i18n.templateDocs : sharedi18n.templateImportDocs;
			menuItems.push(
				<AkDropdownItem key="open-template-browser" onClick={onClickOpenContentCreationPanel}>
					<FormattedMessage {...label} />
				</AkDropdownItem>,
			);
		};

		const addClassifyContentMenuItem = (menuItems: JSX.Element[]) => {
			if (this.props.isClassificationEnabled) {
				menuItems.push(<EllipsisClassificationMenuItem />);
			}
		};

		const addEditLabelsMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<EditLabelDropdownItem contentId={contentId || ''} spaceKey={spaceKey} dialogs={dialogs} />,
			);
		};

		const addMovePageMenuItem = (menuItems, movePageContentId, movePageSpaceKey) => {
			menuItems.push(
				<EditorMovePageButtonLoader
					contentId={movePageContentId}
					spaceKey={movePageSpaceKey}
					appearance="dropdownItem"
					analyticsSource="EllipsisDropdownComponent"
					key="move-page-button"
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
					isUnpublishedDraft={!!isUnpublishedDraft}
				/>,
			);
		};

		const addResolvedCommentsMenuItem = (menuItems: JSX.Element[], contentId, spaceKey) => {
			menuItems.push(
				<AkDropdownItem
					key="resolved-comments"
					onClick={() => {
						if (fg('confluence-frontend-comments-panel')) {
							showCommentsPanel({ openWithView: ViewValues.RESOLVED });
							return;
						}

						dialogs.showDialog(ResolvedInlineCommentsDialogLoader, {
							spaceKey,
							contentId,
							context: 'editor',
						});
					}}
				>
					<MenuItemContainer>
						<FormattedMessage {...sharedi18n.resolvedComments} />{' '}
						<ResolvedInlineCommentsCounterLoader contentId={contentId} />
					</MenuItemContainer>
				</AkDropdownItem>,
			);
		};

		const addDeleteChangesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="delete-changes" onClick={this.saveDraftAnd(showRevertDialog)}>
					{isUnpublishedDraft && contentType === 'page' ? (
						<FormattedMessage {...i18n.deleteUnpublishedPage} />
					) : null}
					{isUnpublishedDraft && contentType !== 'page' ? (
						<FormattedMessage {...i18n.deleteUnpublishedBlogpost} />
					) : null}
					{!isUnpublishedDraft ? <FormattedMessage {...i18n.revert} /> : null}
				</AkDropdownItem>,
			);
		};

		const addSwitchToLiveEditMenuItem = ({
			menuItems: menuItemsArg,
		}: {
			menuItems: React.JSX.Element[];
		}) => {
			if (this.props.isLivePagesEnabled && contentType === 'page') {
				const switchToLiveEditWebItem = this.props.webItems?.find(
					(item) => item.id === 'action-switch-to-live-edit',
				);
				if (!switchToLiveEditWebItem) return;

				menuItemsArg.push(
					<AkDropdownItem key="switch-to-live-edit" onClick={switchToLivePage}>
						{switchToLiveEditWebItem.label}
					</AkDropdownItem>,
				);
			}
		};

		const addPublishGroupMenuItems = (menuItems: JSX.Element[]) => {
			const publishGroupItems = [] as JSX.Element[];

			if (this.isAllowed(Buttons.SWITCH_TO_LIVE_EDIT) && contentId) {
				if (!isUnpublishedDraft) {
					addSwitchToLiveEditMenuItem({
						menuItems: publishGroupItems,
					});
				} else if (saveDraft && fg('confluence_live_pages_switch_draft_to_live_page')) {
					addSwitchToLiveEditMenuItem({
						menuItems: publishGroupItems,
					});
				}
			}
			if (!isUnpublishedDraft) {
				this.isAllowed(Buttons.PUBLISH_WITHOUT_NOTIFY) &&
					publishGroupItems.push(
						<AkDropdownItem
							key="publish-without-notify"
							isDisabled={isDisabled}
							onClick={() => {
								publishWithoutNotify(createAnalyticsEvent, onClickPublish, contentId, contentType);
							}}
						>
							<FormattedMessage {...i18n.updateWithoutNotifying} />
						</AkDropdownItem>,
					);
			}

			publishGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="publish-group-items" key="publish-group-items">
						<AkDropdownItemGroup>{publishGroupItems}</AkDropdownItemGroup>
					</div>,
				);
			return publishGroupItems.length;
		};

		const addOtherGroupMenuItems = (menuItems: JSX.Element[]) => {
			const otherGroupItems = [] as JSX.Element[];
			this.isAllowed(Buttons.PREVIEW) && addPreviewMenuItem(otherGroupItems);
			if (!isUnpublishedDraft && this.isAllowed(Buttons.VIEW_CHANGES)) {
				addViewChangesMenuItem(otherGroupItems);
			}
			// Show option to expose the page template menu that pops out from the side
			if (shouldShowOpenContentCreationMenuItem && this.isAllowed(Buttons.TEMPLATE_BROWSER)) {
				addTemplateBrowserMenuItem(otherGroupItems);
			}
			if (contentId && spaceKey) {
				this.isAllowed(Buttons.CLASSIFY_CONTENT) && addClassifyContentMenuItem(otherGroupItems);
				this.isAllowed(Buttons.EDIT_LABELS) && addEditLabelsMenuItem(otherGroupItems);
				this.isAllowed(Buttons.MOVE_PAGE) &&
					addMovePageMenuItem(otherGroupItems, contentId, spaceKey);
				this.isAllowed(Buttons.RESOLVED_COMMENTS) &&
					addResolvedCommentsMenuItem(otherGroupItems, contentId, spaceKey);
			}

			// Empty title needed to maintain blank line above group
			otherGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="other-group-items" key="other-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{otherGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const addEditorActionsGroupMenuItems = (menuItems: JSX.Element[]): void => {
			const editorActionsGroupItems = [] as JSX.Element[];

			const isAllowed = (contentType === 'page' || contentType === 'blogpost') && hasEditPermission;

			if (!isAllowed || !editorActions) {
				return;
			}

			const {
				canUndo: editorCanUndo,
				canRedo: editorCanRedo,
				undo: editorUndo,
				redo: editorRedo,
				activateFindReplace: editorActivateFindReplace,
			} = editorActions;

			if (!!editorUndo) {
				editorActionsGroupItems.push(
					<AkDropdownItem
						key="editor-undo"
						isDisabled={!editorCanUndo}
						onClick={() => {
							editorUndo?.();
						}}
						elemAfter={
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							<span style={{ color: token('color.icon.subtle') }}>
								{getShortcutString(EDITOR2_SHORTCUTS.UNDO)}
							</span>
						}
					>
						<FormattedMessage {...i18n.undo} />
					</AkDropdownItem>,
				);
			}

			if (!!editorRedo) {
				editorActionsGroupItems.push(
					<AkDropdownItem
						key="editor-redo"
						isDisabled={!editorCanRedo}
						onClick={() => {
							editorRedo?.();
						}}
						elemAfter={
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							<span style={{ color: token('color.icon.subtle') }}>
								{getShortcutString(EDITOR2_SHORTCUTS.REDO)}
							</span>
						}
					>
						<FormattedMessage {...i18n.redo} />
					</AkDropdownItem>,
				);
			}

			if (!!editorActivateFindReplace) {
				editorActionsGroupItems.push(
					<AkDropdownItem
						key="editor-find-replace"
						onClick={() => {
							editorActivateFindReplace?.();
						}}
						elemAfter={
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							<span style={{ color: token('color.icon.subtle') }}>
								{getShortcutString(EDITOR2_SHORTCUTS.FIND_REPLACE)}
							</span>
						}
					>
						<FormattedMessage {...i18n.findReplace} />
					</AkDropdownItem>,
				);
			}

			editorActionsGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="editor-actions-group-items" key="editor-actions-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{editorActionsGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const showRevertEditorDialog = () => {
			dialogs.showDialog(RevertEditorDialog, {
				contentId,
				onPageRevert: () => onClickPageRevert(push, contentType, spaceKey, contentId),
			});
		};

		const addRevertToLegacyEditor = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="revert-editor" onClick={this.saveDraftAnd(showRevertEditorDialog)}>
					<FormattedMessage {...i18n.revertEditor} />
				</AkDropdownItem>,
			);
		};

		const addDiscardGroupMenuItems = (menuItems: JSX.Element[]) => {
			const discardGroupItems = [] as JSX.Element[];

			if (this.isAllowed(Buttons.DELETE_DRAFT)) {
				addDeleteChangesMenuItem(discardGroupItems);
				this.state.isPageAutoconverted &&
					!onEditConvertExp.get('isEnabled', false) &&
					addRevertToLegacyEditor(discardGroupItems);
			}

			// Empty title needed to maintain blank line above group
			discardGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="discard-group-items" key="discard-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{discardGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const shouldShowOpenContentCreationMenuItem =
			!isContentCreationPanelShown && isUnpublishedDraft && contentType === 'page';

		const menuItems = [] as JSX.Element[];

		/**
		 * Menu items are separated into three groups: publish, other and discard
		 * Use the allowedEllipsisButtons array to filter for buttons needed to be rendered
		 */

		addPublishGroupMenuItems(menuItems);
		addEditorActionsGroupMenuItems(menuItems);
		addOtherGroupMenuItems(menuItems);
		addDiscardGroupMenuItems(menuItems);
		addCompanyHubSettingsGroupMenuItems(menuItems);

		return menuItems;
	}

	render() {
		const {
			isDisabled,
			tooltipDisabledState,
			isLivePagesEnabled,
			contentType,
			isUnpublishedDraft,
		} = this.props;

		return (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs) => (
					<>
						{isLivePagesEnabled && contentType === 'page' && !isUnpublishedDraft && (
							<LivePagesExpandedEapConversionSpotlightLoader />
						)}
						<SpotlightTarget name="live-pages-expanded-eap-changeboarding-convert-to-live">
							<div data-testid="ellipsis-menu">
								<CommonDropdownMenu
									isMenuOpen={this.state.isMenuOpen}
									handleMenuOpenChange={this.handleMenuOpenChange}
									isDisabled={isDisabled}
									tooltipDisabledState={tooltipDisabledState}
								>
									{this._getItems(dialogs)}
								</CommonDropdownMenu>
							</div>
						</SpotlightTarget>
					</>
				)}
			</Subscribe>
		);
	}
}

export const EllipsisDropdownComponent = withFlags((props: EllipsisDropdownComponentProps) => {
	const addCompanyHubSettingsGroupMenuItems = useCompanyHubSettingsGroupMenuItems(props);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isEnabled: isClassificationEnabled } = useIsDataClassificationEnabledForOrg();
	const { showCommentsPanel } = useShowCommentsPanel();
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();

	const { push } = useRouteActions();
	const webItemLocation = useWebItemLocation({
		location: 'system.content.action',
		contentId: props.contentId,
	});

	const switchToLivePage = useSwitchToLivePage({
		contentId: props.contentId!,
		spaceId: props.spaceId,
		isUnpublishedDraft: !!props.isUnpublishedDraft,
		flags: props.flags,
		retrieveEditorTitleAndContent: props.retrieveEditorTitleAndContent,
		saveDraft: props.saveDraft,
		analyticsSource: 'ellipsisDropdownComponent',
		adfOrigin: 'EDITOR',
		onSuccessfulConversion: () => {
			// Redirect to the route for the new live page.
			push(
				VIEW_PAGE.toUrl({
					spaceKey: props.spaceKey,
					contentId: props.contentId,
				}),
			);
		},
	});

	return (
		<EllipsisDropdownClass
			{...props}
			addCompanyHubSettingsGroupMenuItems={addCompanyHubSettingsGroupMenuItems}
			createAnalyticsEvent={createAnalyticsEvent}
			isLivePagesEnabled={isLivePagesFeatureEnabled}
			isClassificationEnabled={isClassificationEnabled}
			push={push}
			webItems={webItemLocation?.webItems || []}
			switchToLivePage={switchToLivePage}
			showCommentsPanel={showCommentsPanel}
		/>
	);
});
